<template>
  <div class="editor-header">
    <div class="editor-header__logo-title">
      <i id="close-button" class="fal fa-times editor-header__close-icon" @click="close" />

      <HeroSparkLogo class="editor-header__herospark-logo" />

      <div class="editor-header__logo-text" data-testid="header-title">
        {{ $t('title') }}
      </div>

      <Button
        v-if="isAdmin"
        id="save-theme"
        data-testid="save-theme-button"
        class="editor-header__save-theme-button"
        :disabled="isSavingTheme"
        @click="$bvModal.show(themeModalId)"
      >
        <template v-if="isSavingTheme">
          <Spinner class="editor-header__save-spinner" />
          {{ $t('saving-theme') }}
        </template>
        <template v-else>
          {{ $t('save-theme') }}
        </template>
      </Button>

      <Tooltip target="close-button" :text="$t('close-editor')" />
    </div>

    <div class="editor-header__actions-container">
      <div class="editor-header__devices">
        <div
          id="desktop"
          class="editor-header__device-button"
          data-testid="desktop-view"
          :class="[{ 'editor-header__device-button--active': currentView === 'lg' }]"
          @click="setCurrentView('lg')"
        >
          <i class="fal fa-desktop fa-lg" />
        </div>
        <div
          id="tablet"
          class="editor-header__device-button"
          data-testid="tablet-view"
          :class="[{ 'editor-header__device-button--active': currentView === 'md' }]"
          @click="setCurrentView('md')"
        >
          <i class="fal fa-tablet fa-lg" />
        </div>
        <div
          id="mobile"
          class="editor-header__device-button"
          data-testid="mobile-view"
          :class="[{ 'editor-header__device-button--active': currentView === 'sm' }]"
          @click="setCurrentView('sm')"
        >
          <i class="fal fa-mobile fa-lg" />
        </div>
        <Tooltip target="desktop" :text="$t('desktop')" />
        <Tooltip target="tablet" :text="$t('tablet')" />
        <Tooltip target="mobile" :text="$t('mobile')" />
      </div>

      <div class="editor-header__actions">
        <div class="editor-header__undo-redo-container">
          <div
            id="undo-one"
            class="editor-header__action-button"
            data-testid="undo-one"
            @click="
              undo()
              $root.$emit('bv::hide::tooltip', 'undo-one')
            "
            :class="[{ 'editor-header__action-button--disabled': undoCount === 0 }]"
          >
            <i class="fal fa-undo" />
          </div>

          <div
            id="redo-one"
            class="editor-header__action-button"
            data-testid="redo-one"
            @click="
              redo()
              $root.$emit('bv::hide::tooltip', 'redo-one')
            "
            :class="[{ 'editor-header__action-button--disabled': redoCount === 0 }]"
          >
            <i class="fal fa-redo" />
          </div>

          <Tooltip target="undo-one" :text="$t('undo-one')" />
          <Tooltip target="redo-one" :text="$t('redo-one')" />
        </div>

        <div class="editor-header__save-container">
          <div data-testid="saving-message" v-if="isSavingDraft">
            <Spinner class="editor-header__save-spinner" />
            {{ $t('saving') }}
          </div>
          <div data-testid="save-confirmation-message" v-else>
            <i class="fal fa-check editor-header__save-icon" />
            {{ $t('saved-page') }}
          </div>
        </div>

        <div class="editor-header__tools-container">
          <i
            id="config"
            class="fal fa-cog editor-header__tool-item"
            data-testid="config-icon"
            @click="$bvModal.show('config-modal')"
          />
          <i
            id="code"
            class="fal fa-code editor-header__tool-item"
            data-testid="code-icon"
            @click="$bvModal.show('code-modal')"
          />
          <i id="preview" class="fal fa-eye editor-header__tool-item" data-testid="preview-icon" @click="previewPage" />

          <Tooltip target="config" :text="$t('config')" />
          <Tooltip target="code" :text="$t('code')" />
          <Tooltip target="preview" :text="$t('preview')" />
        </div>

        <div class="editor-header__publish-changes-container">
          <Button
            id="publish-landing"
            class="editor-header__publish-changes-button"
            :disabled="isPublishingOrUnpublishing"
            @click="publishLanding"
          >
            <template v-if="isPublishingOrUnpublishing">
              <Spinner class="editor-header__save-spinner" />
              {{ $t('publishing-changes') }}
            </template>
            <template v-else>
              {{ $t('publish-changes') }}
            </template>
          </Button>

          <Tooltip target="publish-landing" :text="$t('publish-changes-instructions')" />
        </div>

        <div class="editor-header__publish-options">
          <Toggle
            :label="$t('publish')"
            :value="publishedPage"
            :disabled="isPublishingOrUnpublishing"
            @toggle="togglePublish($event)"
          />
        </div>
      </div>
    </div>

    <ConfigModal />
    <CodeModal />
    <ThemeModal v-if="isAdmin" data-testid="theme-modal" :id="themeModalId" @ok="savePageAsTheme" />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import PageCompiler from '@edools/page-compiler'
import { successAlert, errorAlert } from '@/helpers/alertHelper.js'
import jwt from '@/helpers/jwtHelper'
import helpersSettings from '@/helpers/settings'
import Mixin from '../Mixin.js'
import pagesApi from '@/services/PagesApi'
import Spinner from '@/shared/components/Spinner.vue'
import Toggle from '@/shared/components/Toggle.vue'
import Button from '@/shared/components/Button'
import LandingService from '@/services/Landing.js'

export default {
  name: 'Header',
  i18nOptions: { keyPrefix: 'components.header' },
  mixins: [Mixin],
  props: {
    pageId: {
      type: String,
      default: '',
    },
  },
  components: {
    Spinner,
    Toggle,
    Button,
    ConfigModal: () => import('@/components/page-editor/ConfigModal'),
    CodeModal: () => import('@/components/page-editor/CodeModal'),
    HeroSparkLogo: () => import('@/assets/logo-herospark.svg'),
    ThemeModal: () => import('@/components/ThemeModal'),
  },
  mounted() {
    this.publishedPage = this.published

    document.addEventListener('keydown', this.keydownHandler)
  },
  destroyed() {
    document.removeEventListener('keydown', this.keydownHandler)
  },
  computed: {
    ...mapState('landing', ['currentView', 'landing', 'published']),
    ...mapGetters('history', ['undoCount', 'redoCount', 'hasUnsaveChanges']),
    ...mapGetters('landing', ['containers']),
    themeModalId() {
      return 'save-theme-modal'
    },
    isAdmin() {
      return jwt.isAdmin()
    },
  },
  watch: {
    hasUnsaveChanges(unsavedChanges) {
      if (unsavedChanges) this.draftLanding()
    },
  },
  data() {
    return {
      isSavingDraft: false,
      isSavingTheme: false,
      isPublishingOrUnpublishing: false,
      publishedPage: false,
    }
  },
  methods: {
    ...mapActions('landing', ['setCurrentView', 'saveLanding', 'saveDraft', 'setCurrentNavBar']),
    ...mapActions('history', ['undo', 'redo', 'undoAll', 'redoAll']),
    close() {
      const { urlReturnSettings } = helpersSettings
      const returnUrl = localStorage.getItem(urlReturnSettings.key)
      if (returnUrl) {
        window.location = returnUrl
      } else {
        this.$router.push({ name: 'my-pages' })
      }
    },
    savePageAsTheme({ name, category, description, locale }) {
      this.$bvModal.hide(this.themeModalId)
      this.isSavingTheme = true
      const payload = {
        id: this.pageId,
        name,
        category,
        description,
        locale,
      }
      LandingService.savePageAsTheme(payload)
        .then(({ data }) => {
          if (data.errors) {
            errorAlert(this.$t('message.save-theme-error'))
            return
          }
          successAlert(this.$t('message.save-theme-success'))
        })
        .catch(() => errorAlert(this.$t('message.save-theme-error')))
        .finally(() => (this.isSavingTheme = false))
    },
    togglePublish(publish) {
      this.publishedPage = publish

      this.publishedPage ? this.publishLanding() : this.unpublishLanding()
    },
    keydownHandler(event) {
      if ((event.ctrlKey || event.metaKey) && event.code === 'KeyZ') {
        this.undo()
        this.$root.$emit('bv::hide::tooltip', 'undo-one')
      } else if ((event.ctrlKey || event.metaKey) && event.code === 'KeyY') {
        event.preventDefault()
        this.redo()
        this.$root.$emit('bv::hide::tooltip', 'redo-one')
      } else if ((event.ctrlKey || event.metaKey) && event.code === 'KeyS') {
        event.preventDefault()
        this.draftLanding()
      }
    },
    previewPage() {
      window.open(`/editor/${this.pageId}/preview`, '_blank').focus()
    },
    draftLanding() {
      this.isSavingDraft = true
      this.saveDraft({
        id: this.pageId,
        draft: this.$store.state.landing.landing,
        settings: this.$store.state.settings,
      })
        .then(({ data }) => {
          if (data.errors) {
            errorAlert(this.$t('message.save-error'))
            return
          }
        })
        .catch(() => {
          errorAlert(this.$t('message.save-error'))
        })
        .finally(() => (this.isSavingDraft = false))
    },
    async publishLanding() {
      this.isPublishingOrUnpublishing = true

      this.$root.$emit('bv::hide::tooltip', 'publish-landing')

      const { compiled, preCompiled } = PageCompiler.compile(this.$store.state.landing.landing)

      this.saveLanding({
        id: this.pageId,
        published: true,
        landing: this.$store.state.landing.landing,
        settings: this.$store.state.settings,
        preCompiled: PageCompiler.build(preCompiled, this.$store.state.settings),
        compiled: PageCompiler.build(compiled, this.$store.state.settings),
      })
        .then(() => {
          successAlert(this.$t('message.publish-success'))
        })
        .catch(() => {
          errorAlert(this.$t('message.publish-error'))
        })
        .finally(() => {
          this.isPublishingOrUnpublishing = false
          this.publishedPage = true
        })
    },
    async unpublishLanding() {
      this.isPublishingOrUnpublishing = true

      pagesApi
        .savePage({ id: this.pageId, published: false })
        .then(() => successAlert(this.$t('message.unpublish-success')))
        .catch(() => errorAlert(this.$t('message.unpublish-error')))
        .finally(() => (this.isPublishingOrUnpublishing = false))
    },
  },
}
</script>

<style lang="scss" scoped>
.editor-header {
  padding-left: 24px;
  padding-right: 24px;
  height: 64px;
  width: 100%;
  display: flex;
  align-items: center;
  background: white;
  color: $grey;
  border: 1px solid $grey-25;
}

.editor-header__close-icon {
  cursor: pointer;
}

.editor-header__herospark-logo {
  margin-left: 16px;
}

.editor-header__logo-title {
  min-width: 336px;
  display: flex;
  align-items: center;
}

.editor-header__save-theme-button {
  width: fit-content;
  margin-left: 24px;
}

.editor-header__actions-container {
  display: flex;
  height: 100%;
  width: 100%;
}

.editor-header__devices {
  display: flex;
  align-items: center;
}

.editor-header__device-button {
  color: $grey-25;
  margin-right: 16px;
  height: 100%;
  display: flex;
  align-items: center;
  min-width: 24px;
  justify-content: center;
  cursor: pointer;
}

.editor-header__device-button--active {
  color: $grey;
  border-bottom: 4px solid $cherry;
}

.editor-header__logo-text {
  margin-left: 12px;
}

.editor-header__actions {
  display: grid;
  grid-template-areas: 'undoredo save tools publish-changes publish';
  grid-template-columns: 50px auto 135px 160px 130px;
  grid-gap: 40px;
  width: 100%;
}

.editor-header__undo-redo-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-area: undoredo;
}

.editor-header__action-button {
  color: $grey;
  cursor: pointer;
}

.editor-header__save-container {
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  grid-area: save;
}

.editor-header__save-spinner {
  margin-right: 8px;
}

.editor-header__save-icon {
  color: #34b469;
  font-size: 1rem;
  margin-right: 4px;
}

.editor-header__tools-container {
  display: flex;
  align-items: center;
  grid-area: tools;
}

.editor-header__tool-item {
  cursor: pointer;
  margin-left: 24px;
}

.editor-header__publish-options {
  display: flex;
  align-items: center;
  grid-area: publish;
}

.editor-header__publish-changes-container {
  grid-area: publish-changes;
  display: flex;
  align-items: center;
}

.editor-header__publish-changes-button {
  height: 30px;
  width: auto;
}
</style>
